import { Button } from '@components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utils'
import {
  PHOTO_PLACEHOLDER_ACTION_IDS,
  type PhotoActionDefinition,
  type PhotoActionId,
} from '@/lib/photos/photo-action-items'
import {
  PHOTO_ACTION_ICONS,
  PHOTO_ACTION_LOADING_ICON,
  type PhotoActionHandlers,
} from '@/components/app/photos/photo-actions-list'

interface PhotoActionButtonProps {
  action: PhotoActionDefinition
  handlers: PhotoActionHandlers
  isDisabled: (id: PhotoActionId) => boolean
  isRemovingBackground: boolean
  isActionActive?: (id: PhotoActionId) => boolean
}

/**
 * One action in the toolbar: an icon button with a tooltip, except Remove Background, which is a
 * labelled button (and shows a spinner while it runs).
 */
export function PhotoActionButton({
  action: { id, label, icon, toolbarLabel },
  handlers,
  isDisabled,
  isRemovingBackground,
  isActionActive,
}: PhotoActionButtonProps) {
  const Icon = PHOTO_ACTION_ICONS[icon]
  const onClick = handlers[id]
  const disabled = isDisabled(id) || (!onClick && !PHOTO_PLACEHOLDER_ACTION_IDS.has(id))
  const showLoading = id === 'removeBackground' && isRemovingBackground
  const isRemoveBg = id === 'removeBackground'
  const displayLabel = toolbarLabel ?? label
  const active = isActionActive?.(id) ?? false
  const expandsEffects = id === 'photoEffects'

  const button = (
    <Button
      type="button"
      variant="ghost"
      size={isRemoveBg ? 'sm' : 'icon'}
      disabled={disabled}
      onClick={onClick ?? undefined}
      className={cn(
        'h-8 shrink-0 text-xs font-medium text-foreground',
        isRemoveBg ? 'gap-2 px-3 whitespace-nowrap' : 'size-8',
        active && 'bg-accent text-accent-foreground'
      )}
      aria-busy={showLoading || undefined}
      aria-label={displayLabel}
      aria-pressed={active || undefined}
      aria-expanded={expandsEffects ? active : undefined}
    >
      {showLoading ? (
        <PHOTO_ACTION_LOADING_ICON className="size-4 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <Icon className="size-4 shrink-0" aria-hidden="true" />
      )}
      {isRemoveBg ? <span>{displayLabel}</span> : null}
    </Button>
  )

  if (isRemoveBg) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">{button}</span>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  )
}
